import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import { convertInputToUTC } from "../../util/date";
import AddGamesBulkMutation from "./mutation/BulkAddGames";
import useThemeColors from "../organization/portal/useThemeColors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const makeRowKey = () =>
    `row_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

const emptyGame = (stages = []) => {
    const firstStage = stages?.[0] || null;
    const firstGroup = firstStage?.groups?.[0] || null;

    return {
        _rowKey: makeRowKey(),
        stageId: firstStage?.id || "",
        groupId: firstGroup?.id || "",
        description: "",
        dateTime: "",
        homeTeamId: "",
        visitorTeamId: "",
        fieldId: "",
        refereeIds: [],
        managerIds: [],
        matchDay: "",
        teams: firstGroup?.teams || firstStage?.teams || [],
        fields: firstStage?.fields || [],
        groups: firstStage?.groups || [],
    };
};

export default function BulkAddGamesModal({
                                              modalId = "bulk-add-games-modal",
                                              stages = [],
                                              referees = [],
                                              managers = [],
                                          }) {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const [addGamesBulk] = AddGamesBulkMutation();
    const [games, setGames] = useState(() => [emptyGame(stages)]);
    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState(null);
    const [showRightScrollCue, setShowRightScrollCue] = useState(false);
    const tableWrapRef = useRef(null);

    const stageOptions = useMemo(() => (Array.isArray(stages) ? stages : []), [stages]);
    const managerOptions = useMemo(() => (Array.isArray(managers) ? managers : []), [managers]);
    const safeGames = useMemo(() => (Array.isArray(games) ? games : []), [games]);
    const safeValidationErrors = useMemo(
        () => (Array.isArray(validationErrors) ? validationErrors : []),
        [validationErrors]
    );

    const scopeSel = useMemo(() => `#${modalId}`, [modalId]);

    useEffect(() => {
        if (!games?.length) return;
        const onlyRow = games[0];
        const hasNoStageSelected = games.length === 1 && !onlyRow?.stageId && stages?.length;

        if (hasNoStageSelected) {
            setGames([emptyGame(stages)]);
            setValidationErrors(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stages]);

    const updateRightScrollCue = useCallback(() => {
        const el = tableWrapRef.current;
        if (!el) {
            setShowRightScrollCue(false);
            return;
        }

        const canScrollHorizontally = el.scrollWidth > el.clientWidth + 1;
        const isAtRightEdge = el.scrollLeft + el.clientWidth >= el.scrollWidth - 2;
        setShowRightScrollCue(canScrollHorizontally && !isAtRightEdge);
    }, []);

    useEffect(() => {
        const handleShown = () => {
            setValidationErrors(null);
            requestAnimationFrame(() => requestAnimationFrame(updateRightScrollCue));
        };

        if (window.$ && typeof window.$(scopeSel)?.on === "function") {
            window.$(scopeSel).on("shown.bs.modal", handleShown);
            return () => window.$(scopeSel).off("shown.bs.modal", handleShown);
        }
    }, [scopeSel, updateRightScrollCue]);

    useEffect(() => {
        const el = tableWrapRef.current;
        if (!el) return;

        updateRightScrollCue();

        el.addEventListener("scroll", updateRightScrollCue, { passive: true });
        window.addEventListener("resize", updateRightScrollCue);

        return () => {
            el.removeEventListener("scroll", updateRightScrollCue);
            window.removeEventListener("resize", updateRightScrollCue);
        };
    }, [updateRightScrollCue]);

    useEffect(() => {
        updateRightScrollCue();
    }, [updateRightScrollCue, games, stages]);

    const addGameRow = useCallback(() => {
        setGames((prev) => [...prev, emptyGame(stages)]);
        setValidationErrors(null);
    }, [stages]);

    const removeGameRow = useCallback((rowKey) => {
        setGames((prev) => prev.filter((g) => g._rowKey !== rowKey));
        setValidationErrors(null);
    }, []);

    const updateGameField = useCallback(
        (rowKey, key, value) => {
            setGames((prev) =>
                prev.map((game) => {
                    if (game._rowKey !== rowKey) return game;

                    const sanitizedValue =
                        key === "matchDay" ? String(value || "").replace(/\D/g, "") : value;

                    let next = { ...game, [key]: sanitizedValue };

                    if (key === "stageId") {
                        const selectedStage = stageOptions.find((s) => s.id === value) || null;

                        next.groups = selectedStage?.groups || [];

                        if (selectedStage?.groups?.length) {
                            const firstGroup = selectedStage.groups[0];
                            next.groupId = firstGroup?.id || "";
                            next.teams = firstGroup?.teams || [];
                        } else {
                            next.groupId = "";
                            next.teams = selectedStage?.teams || [];
                        }

                        next.fields = selectedStage?.fields || [];
                        next.homeTeamId = "";
                        next.visitorTeamId = "";
                        next.fieldId = "";
                    }

                    if (key === "groupId") {
                        next.groupId = value;

                        const selectedGroup = (game.groups || []).find((g) => g.id === value) || null;
                        next.teams = selectedGroup?.teams || [];

                        next.homeTeamId = "";
                        next.visitorTeamId = "";
                    }

                    return next;
                })
            );

            setValidationErrors(null);
        },
        [stageOptions]
    );

    const validateRows = useCallback(() => {
        const errors = [];

        safeGames.forEach((g, i) => {
            if (
                !g.stageId ||
                !g.dateTime ||
                !g.homeTeamId ||
                !g.visitorTeamId ||
                !g.fieldId ||
                !g.matchDay
            ) {
                errors.push({
                    row: i + 1,
                    rowKey: g._rowKey,
                    error: t("bulkAddGamesModal.validation.completeAllFields"),
                });
            }
            if (g.homeTeamId && g.homeTeamId === g.visitorTeamId) {
                errors.push({
                    row: i + 1,
                    rowKey: g._rowKey,
                    error: t("bulkAddGamesModal.validation.teamsMustBeDifferent"),
                });
            }
        });

        return errors;
    }, [safeGames, t]);

    const handleBulkSubmit = async (e) => {
        e.preventDefault();
        setValidationErrors(null);

        const errors = validateRows();
        if (errors.length) {
            setValidationErrors(errors);
            return;
        }

        setSaving(true);

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        try {
            const toSend = games.map((g) => ({
                stageId: g.stageId,
                groupId: g.groupId || null,
                description: g.description || "",
                fieldId: g.fieldId,
                homeTeamId: g.homeTeamId,
                visitorTeamId: g.visitorTeamId,
                dateTime: convertInputToUTC(g.dateTime, timezone),
                timezone,
                matchDay: g.matchDay,
                refereeIds: g.refereeIds || [],
                managerIds: g.managerIds || [],
            }));

            const result = await addGamesBulk({ variables: { games: toSend } });

            if (result.data?.addGamesBulk?.success) {
                toast.success(t("bulkAddGamesModal.messages.gamesCreatedSuccessfully"), {
                    autoClose: 2500,
                });

                setGames([emptyGame(stages)]);

                if (window.$ && typeof window.$(scopeSel)?.modal === "function") {
                    window.$(scopeSel).modal("hide");
                }

                setTimeout(() => window.location.reload(), 3000);
            } else {
                let errs = result.data?.addGamesBulk?.errors || [];
                if (errs.length && typeof errs[0] === "string") {
                    errs = errs.map((errStr) => {
                        try {
                            return JSON.parse(errStr);
                        } catch {
                            return { row: "-", error: errStr };
                        }
                    });
                }
                setValidationErrors(errs);
            }
        } catch (err) {
            toast.error(
                t("bulkAddGamesModal.messages.errorCreatingGames", {
                    message: getLocalizedGraphQLError(err, t, "bulkAddGamesModal.messages.errorCreatingGames"),
                })
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="modal fade kbola-bulkModal"
            id={modalId}
            tabIndex="-1"
            role="dialog"
            aria-labelledby="bulkAddGamesTitle"
            aria-hidden="true"
        >
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content kbola-modalContent">
                    <form onSubmit={handleBulkSubmit}>
                        <div className="modal-header kbola-modalHeader">
                            <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                                <span className="kbola-pill kbola-pillBrand">
                                    {t("bulkAddGamesModal.bulk")}
                                </span>
                                <h4 className="modal-title mb-0 kbola-modalTitle" id="bulkAddGamesTitle">
                                    {t("bulkAddGamesModal.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                className="close kbola-closeBtn"
                                data-dismiss="modal"
                                aria-label={t("bulkAddGamesModal.close")}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body kbola-modalBody">
                            {safeValidationErrors.length > 0 && (
                                <div className="kbola-alertDanger">
                                    <div className="kbola-alertTitle">
                                        {t("bulkAddGamesModal.alerts.fixFollowing")}
                                    </div>
                                    <ul className="kbola-alertList">
                                        {safeValidationErrors.map((err) => (
                                            <li key={`${err.rowKey || "row"}_${err.row}_${err.error}`}>
                                                <strong>
                                                    {t("bulkAddGamesModal.alerts.rowLabel", {
                                                        row: err.row,
                                                    })}
                                                </strong>{" "}
                                                {err.error}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div
                                ref={tableWrapRef}
                                className={`table-responsive kbola-tableWrap ${
                                    showRightScrollCue ? "kbola-tableWrap--hasRightCue" : ""
                                }`}
                            >
                                <table className="table table-striped table-sm mb-0 kbola-table">
                                    <thead className="kbola-thead">
                                    <tr>
                                        <th>{t("bulkAddGamesModal.headers.stage")}</th>
                                        <th>{t("bulkAddGamesModal.headers.group")}</th>
                                        <th>{t("bulkAddGamesModal.headers.datetime")}</th>
                                        <th>{t("bulkAddGamesModal.headers.away")}</th>
                                        <th>{t("bulkAddGamesModal.headers.home")}</th>
                                        <th>{t("bulkAddGamesModal.headers.venue")}</th>
                                        <th>{t("bulkAddGamesModal.headers.responsible")}</th>
                                        <th>{t("bulkAddGamesModal.headers.manager")}</th>
                                        <th>{t("bulkAddGamesModal.headers.matchday")}</th>
                                        <th>{t("bulkAddGamesModal.headers.description")}</th>
                                        <th className="text-right"></th>
                                    </tr>
                                    </thead>

                                    <tbody className="kbola-tbody">
                                    {safeGames.map((g, idx) => {
                                        const errorForRow =
                                            safeValidationErrors.find((e) => e.row === idx + 1);

                                        return (
                                            <tr key={g._rowKey} className={errorForRow ? "kbola-rowError" : ""}>
                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={g.stageId}
                                                        onChange={(e) =>
                                                            updateGameField(g._rowKey, "stageId", e.target.value)
                                                        }
                                                        required
                                                    >
                                                        <option key="stage_choose" value="">
                                                            {t("bulkAddGamesModal.choose")}
                                                        </option>
                                                        {stageOptions.map((s) => (
                                                            <option key={`stage_${s.id}`} value={s.id}>
                                                                {s.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                {Array.isArray(g.groups) && g.groups.length > 0 ? (
                                                    <td>
                                                        <select
                                                            className="form-control kbola-control"
                                                            value={g.groupId}
                                                            onChange={(e) =>
                                                                updateGameField(g._rowKey, "groupId", e.target.value)
                                                            }
                                                            required
                                                        >
                                                            <option key="group_choose" value="">
                                                                {t("bulkAddGamesModal.choose")}
                                                            </option>
                                                            {(Array.isArray(g.groups) ? g.groups : []).map((grp) => (
                                                                <option key={`group_${grp.id}`} value={grp.id}>
                                                                    {grp.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </td>
                                                ) : (
                                                    <td>
                                                            <span className="kbola-pill kbola-pillMuted">
                                                                {t("bulkAddGamesModal.notAvailable")}
                                                            </span>
                                                    </td>
                                                )}

                                                <td>
                                                    <input
                                                        type="datetime-local"
                                                        className="form-control kbola-control"
                                                        value={g.dateTime}
                                                        onChange={(e) =>
                                                            updateGameField(g._rowKey, "dateTime", e.target.value)
                                                        }
                                                        required
                                                    />
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={g.visitorTeamId}
                                                        onChange={(e) =>
                                                            updateGameField(
                                                                g._rowKey,
                                                                "visitorTeamId",
                                                                e.target.value
                                                            )
                                                        }
                                                        required
                                                    >
                                                        <option key="visitor_choose" value="">
                                                            {t("bulkAddGamesModal.choose")}
                                                        </option>
                                                        {(Array.isArray(g.teams) ? g.teams : []).map((tItem) => {
                                                            const value = String(tItem.teamId ?? tItem.id ?? "");
                                                            const label =
                                                                tItem.teamName ??
                                                                tItem.name ??
                                                                t("bulkAddGamesModal.teamFallback");
                                                            return (
                                                                <option key={`visitor_${value}`} value={value}>
                                                                    {label}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={g.homeTeamId}
                                                        onChange={(e) =>
                                                            updateGameField(
                                                                g._rowKey,
                                                                "homeTeamId",
                                                                e.target.value
                                                            )
                                                        }
                                                        required
                                                    >
                                                        <option key="home_choose" value="">
                                                            {t("bulkAddGamesModal.choose")}
                                                        </option>
                                                        {(Array.isArray(g.teams) ? g.teams : []).map((tItem) => {
                                                            const value = String(tItem.teamId ?? tItem.id ?? "");
                                                            const label =
                                                                tItem.teamName ??
                                                                tItem.name ??
                                                                t("bulkAddGamesModal.teamFallback");
                                                            return (
                                                                <option key={`home_${value}`} value={value}>
                                                                    {label}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={g.fieldId}
                                                        onChange={(e) =>
                                                            updateGameField(g._rowKey, "fieldId", e.target.value)
                                                        }
                                                        required
                                                    >
                                                        <option key="field_choose" value="">
                                                            {t("bulkAddGamesModal.choose")}
                                                        </option>
                                                        {(Array.isArray(g.fields) ? g.fields : []).map((f) => (
                                                            <option key={`field_${f.id}`} value={f.id}>
                                                                {f.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={g.refereeIds}
                                                        multiple
                                                        onChange={(e) =>
                                                            updateGameField(g._rowKey, "refereeIds", Array.from(e.target.selectedOptions).map(o => o.value))
                                                        }
                                                    >
                                                        {(Array.isArray(stageOptions.find((stage) => String(stage.id) === String(g.stageId))?.stageReferees) ? stageOptions.find((stage) => String(stage.id) === String(g.stageId)).stageReferees : []).map((r) => (
                                                            <option key={`ref_${r.id}`} value={r.id}>
                                                                {r.fullName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <select
                                                        className="form-control kbola-control"
                                                        value={g.managerIds}
                                                        multiple
                                                        onChange={(e) =>
                                                            updateGameField(g._rowKey, "managerIds", Array.from(e.target.selectedOptions).map(o => o.value))
                                                        }
                                                    >
                                                        {managerOptions.map((r) => (
                                                            <option key={`manager_${r.id}`} value={r.id}>
                                                                {r.fullName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </td>

                                                <td>
                                                    <input
                                                        type="number"
                                                         className="form-control kbola-control kbola-matchdayControl"
                                                         value={String(g.matchDay || "")}
                                                         onChange={(e) =>
                                                             updateGameField(g._rowKey, "matchDay", e.target.value)
                                                         }
                                                         min="1"
                                                         step="1"
                                                         inputMode="numeric"
                                                         placeholder={t("bulkAddGamesModal.headers.matchday")}
                                                         required
                                                     />
                                                </td>

                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-control kbola-control kbola-descriptionControl"
                                                        value={g.description || ""}
                                                        onChange={(e) =>
                                                            updateGameField(g._rowKey, "description", e.target.value)
                                                        }
                                                        placeholder={t("bulkAddGamesModal.placeholders.description")}
                                                    />
                                                </td>

                                                <td className="text-right">
                                                    {games.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="btn kbola-iconDanger"
                                                            onClick={() => removeGameRow(g._rowKey)}
                                                            title={t("bulkAddGamesModal.removeRow")}
                                                        >
                                                            <i className="fas fa-trash" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex align-items-center justify-content-between mt-3">
                                <button
                                    type="button"
                                    className="btn kbola-addRowBtn"
                                    onClick={addGameRow}
                                >
                                    <i className="fas fa-plus mr-2" />
                                    {t("bulkAddGamesModal.buttons.addAnotherGame")}
                                </button>

                                <div className="kbola-hint">{t("bulkAddGamesModal.hint")}</div>
                            </div>
                        </div>

                        <div className="modal-footer kbola-modalFooter">
                            <button
                                type="button"
                                className="btn btn-default kbola-secondaryBtn"
                                data-dismiss="modal"
                            >
                                {t("bulkAddGamesModal.close")}
                            </button>

                            <button
                                type="submit"
                                className="btn btn-kbola kbola-primaryBtn"
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span
                                            className="spinner-border spinner-border-sm mr-2"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        {t("bulkAddGamesModal.buttons.saving")}
                                    </>
                                ) : (
                                    t("bulkAddGamesModal.buttons.saveAll")
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <style>{`
        ${scopeSel} {
          font-family: 'Poppins', sans-serif;
        }

        ${scopeSel} i.fas,
        ${scopeSel} i.far,
        ${scopeSel} i.fal,
        ${scopeSel} i.fa,
        ${scopeSel} i[class^="fa-"],
        ${scopeSel} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        ${scopeSel} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        ${scopeSel} .kbola-modalContent {
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
        }

        ${scopeSel} .kbola-modalHeader {
          padding: 14px 16px;
          background: ${colors.primary};
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        ${scopeSel} .kbola-modalTitle {
          font-size: 16px;
          font-weight: 900;
          color: ${colors.textOnPrimary};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ${scopeSel} .kbola-closeBtn {
          color: ${colors.textOnPrimary};
          opacity: 1;
        }

        ${scopeSel} .kbola-modalBody {
          background: #fff;
        }

        ${scopeSel} .kbola-modalFooter {
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          background: rgba(0,0,0,0.01);
        }

        ${scopeSel} .kbola-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          line-height: 1;
          white-space: nowrap;
        }
        ${scopeSel} .kbola-pillBrand {
          background: ${colors.textOnPrimary};
          color: ${colors.primary};
          border-color: rgba(0,0,0,0.10);
        }
        ${scopeSel} .kbola-pillMuted {
          background: rgba(0,0,0,0.03);
          color: rgba(20,21,26,0.72);
          border-color: rgba(0,0,0,0.08);
          font-weight: 800;
        }

        ${scopeSel} .kbola-alertDanger {
          border-radius: 14px;
          border: 1px solid rgba(220,53,69,0.18);
          background: rgba(220,53,69,0.06);
          padding: 12px 14px;
          margin-bottom: 14px;
        }
        ${scopeSel} .kbola-alertTitle {
          font-weight: 900;
          color: rgba(220,53,69,0.95);
          margin-bottom: 6px;
        }
        ${scopeSel} .kbola-alertList {
          margin: 0;
          padding-left: 18px;
          color: rgba(20,21,26,0.85);
          font-weight: 700;
          font-size: 13px;
        }

        ${scopeSel} .kbola-tableWrap {
          position: relative;
          border-radius: 16px;
          overflow-x: auto;
          overflow-y: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 10px 26px rgba(0,0,0,0.06);
          -webkit-overflow-scrolling: touch;
        }
        ${scopeSel} .kbola-tableWrap--hasRightCue::after {
          content: "";
          position: absolute;
          top: 0;
          right: 0;
          width: 96px;
          height: 100%;
          pointer-events: none;
          background: linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1));
          z-index: 2;
        }
        ${scopeSel} .kbola-tableWrap--hasRightCue::before {
          content: ">>>";
          position: absolute;
          top: 50%;
          right: 14px;
          transform: translateY(-50%);
          pointer-events: none;
          font-size: 30px;
          font-weight: 900;
          letter-spacing: 0.08em;
          color: ${colors.woodsmoke};
          text-shadow: 0 0 6px rgba(255,255,255,0.95);
          animation: kbola-scrollCuePulse 1s ease-in-out infinite;
          z-index: 3;
        }
        @keyframes kbola-scrollCuePulse {
          0% { opacity: 0.35; }
          50% { opacity: 0.9; }
          100% { opacity: 0.35; }
        }
        
        ${scopeSel} .kbola-table {
          min-width: 1160px;
        }

        ${scopeSel} .kbola-thead th {
          background: rgba(0,0,0,0.02);
          border-bottom: 1px solid rgba(0,0,0,0.06);
          color: rgba(20,21,26,0.65);
          font-size: 12.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          padding: 12px 14px;
          white-space: nowrap;
          vertical-align: middle;
        }

        ${scopeSel} .kbola-tbody td {
          border-top: 1px solid rgba(0,0,0,0.06);
          padding: 10px 12px;
          vertical-align: middle;
          background: #fff;
        }

        ${scopeSel} .kbola-tbody tr:hover td {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.08);
        }

        ${scopeSel} .kbola-rowError td {
          background: rgba(220,53,69,0.06) !important;
        }

        ${scopeSel} .kbola-control {
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          font-weight: 800;
          font-size: 14px;
          padding: 10px 12px;
          height: 48px;
          box-shadow: none;
        }
        ${scopeSel} select.kbola-control {
          min-width: 150px;
        }
        ${scopeSel} input[type="datetime-local"].kbola-control {
          min-width: 170px;
        }
        ${scopeSel} .kbola-matchdayControl {
          min-width: 130px;
        }
        ${scopeSel} .kbola-descriptionControl {
          min-width: 260px;
        }
        ${scopeSel} .kbola-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75);
        }

        ${scopeSel} .kbola-primaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
          background: ${colors.primary};
          border: 1px solid rgba(0,0,0,0.10);
          color: ${colors.textOnPrimary};
        }

        ${scopeSel} .kbola-secondaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
        }

        ${scopeSel} .kbola-addRowBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
          background: ${colors.primary};
          border: 1px solid rgba(0,0,0,0.10);
          color: ${colors.textOnPrimary};
        }
        ${scopeSel} .kbola-addRowBtn:hover {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 1);
        }

        ${scopeSel} .kbola-iconDanger {
          width: 42px;
          height: 42px;
          border-radius: 12px;
          border: 1px solid rgba(220,53,69,0.18);
          background: rgba(220,53,69,0.06);
          color: rgba(220,53,69,0.95);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
        }
        ${scopeSel} .kbola-iconDanger:hover {
          background: rgba(220,53,69,0.10);
        }

        ${scopeSel} .kbola-hint {
          font-size: 12px;
          font-weight: 800;
          color: rgba(20,21,26,0.55);
        }
        
        ${scopeSel} .modal-dialog {
          width: calc(100vw - 2rem);
          max-width: 1600px;
          max-height: calc(100% - 1.75rem * 2);
        }
        
        ${scopeSel} .kbola-modalBody {
          max-height: calc(100vh - 210px);
          overflow-y: auto;
        }

        @media (max-width: 576px) {
          ${scopeSel} .modal-dialog {
            width: calc(100vw - 1rem);
            margin: 0.5rem auto;
          }
          ${scopeSel} .kbola-hint { display: none; }
          ${scopeSel} .kbola-thead th { padding: 10px 10px; }
          ${scopeSel} .kbola-tbody td { padding: 10px 10px; }
        }
      `}</style>
        </div>
    );
}
